//talks to device apis to schedule notifications
import { useBackgroundLocation } from "@/mobile_app/services/background-location-service"
import { LocalNotifications, type ActionPerformed } from "@capacitor/local-notifications"
import { ref } from "vue"
import { Capacitor, type PluginListenerHandle } from "@capacitor/core"
const { trackLocation, stopLocationTracking } = useBackgroundLocation()

const activeTitle = ref("")
const startTime = ref(0)
let notificationActionListener: PluginListenerHandle | null = null
let notificationInitialized = false

const WORK_TRACKING_NOTIFICATION_ID = 1

/**
 * Format the start time as a string
 * @param timestamp - The timestamp to format
 * @returns The formatted start time
 */
function formatStartTime(timestamp: number): string {
  const date = new Date(timestamp)
  const hours = date.getHours().toString().padStart(2, "0")
  const minutes = date.getMinutes().toString().padStart(2, "0")
  return `${hours}:${minutes}`
}

/**
 * Initialize notification channel and action types on first use
 * @returns void
 */
async function initializeNotifications() {
  if (!Capacitor.isNativePlatform() || notificationInitialized) {
    return
  }

  try {
    // Register notification channel for Android with maximum importance
    if (Capacitor.getPlatform() === "android") {
      await LocalNotifications.createChannel({
        id: "work_tracking",
        name: "Work Tracking",
        description: "Notifications for active work tracking",
        importance: 5, // MAX importance
        visibility: 1, // Public visibility
        lights: false,
        vibration: false,
        sound: undefined
      })
    }

    // Android & iOS
    await LocalNotifications.registerActionTypes({
      types: [
        {
          id: "MANAGE_WORK_PERIOD",
          actions: [
            {
              id: "manage",
              title: "🕔 Bekijk dienst",
              // https://developer.apple.com/documentation/usernotifications/unnotificationactionoptions
              foreground: true // The action causes the app to launch in the foreground.
              // destructive: true // This will typically make the button red on iOS
            }
          ]
        }
      ]
    })

    notificationInitialized = true
  } catch (error) {
    console.warn("Failed to initialize notifications:", error)
  }
}

/**
 * Update the notification with the current start time
 * @returns void
 */
async function updateNotification() {
  if (startTime.value === 0) return

  const startTimeFormatted = formatStartTime(startTime.value)
  await scheduleNotification(`Sinds ${startTimeFormatted} heb je een dienst lopen`, "")
}

/**
 * Schedule a notification with the given title and body
 * @param title - The title of the notification
 * @param body - The body of the notification
 * @returns void
 */
async function scheduleNotification(title: string, body: string) {
  // Only schedule notifications on native platforms
  if (!Capacitor.isNativePlatform()) {
    return
  }

  // Initialize notifications on first use
  await initializeNotifications()

  // Ensure notification permissions before scheduling
  const permission = await LocalNotifications.checkPermissions()
  if (permission.display !== "granted") {
    const requested = await LocalNotifications.requestPermissions()
    if (requested.display !== "granted") {
      console.warn("Notifications permission not granted")
      return
    }
  }
  // Cancel existing notifications
  await LocalNotifications.cancel({ notifications: [{ id: WORK_TRACKING_NOTIFICATION_ID }] })
  // Android & iOS
  await LocalNotifications.schedule({
    notifications: [
      {
        title: title,
        body: body,
        id: WORK_TRACKING_NOTIFICATION_ID, // Fixed ID to make it easier to manage
        schedule: { at: new Date(Date.now() + 1000) },
        actionTypeId: "MANAGE_WORK_PERIOD",
        sound: undefined,
        extra: {
          type: "work_start",
          workTitle: title // Store title for potential reuse
        },
        ...(Capacitor.getPlatform() === "android"
          ? {
              ongoing: true, // Makes notification persistent
              autoCancel: false, // Prevents auto-cancellation when clicked
              iconColor: "#F06C04",
              // largeIcon: 'res://drawable/icon_logo48',
              smallIcon: "ic_launcher_foreground",
              channelId: "work_tracking"
            }
          : {})
      }
    ]
  })
}

async function startTracking(title: string, workPeriodStartTime?: number | Date) {
  activeTitle.value = title
  // Use work period start time if provided, otherwise use current time
  startTime.value = workPeriodStartTime
    ? workPeriodStartTime instanceof Date
      ? workPeriodStartTime.getTime()
      : workPeriodStartTime
    : Date.now()

  // Start tracking the location
  await trackLocation()

  // Update the notification with the current start time
  await updateNotification()

  // Only add notification listener on native platforms
  if (Capacitor.isNativePlatform()) {
    // Remove any existing listener before adding a new one to prevent accumulation
    if (notificationActionListener) {
      await notificationActionListener.remove()
      notificationActionListener = null
    }

    // Listener fires when the user taps the notification or one of its actions
    notificationActionListener = await LocalNotifications.addListener(
      "localNotificationActionPerformed",
      async (notificationAction: ActionPerformed) => {
        console.log("Notification action performed", notificationAction)
        // Handle action action
        // if (notificationAction.actionId === "manage") {
        //   return
        // }
        // Update the notification with the current start time
        await updateNotification()
      }
    )
  }
}

/**
 * Stop tracking the location and cancel all pending notifications
 * @returns void
 */
async function stopTracking() {
  startTime.value = 0

  // Only handle notifications on native platforms
  if (Capacitor.isNativePlatform()) {
    // Remove the notification action listener to prevent memory leaks
    if (notificationActionListener) {
      await notificationActionListener.remove()
      notificationActionListener = null
    }

    // Get all pending notifications first
    const pending = await LocalNotifications.getPending()

    // Cancel all pending notifications if there are any
    if (pending && pending.notifications && pending.notifications.length > 0) {
      await LocalNotifications.cancel({ notifications: pending.notifications })
    }

    // Remove our delivered notifications
    const delivered = await LocalNotifications.getDeliveredNotifications()
    const ourDelivered =
      delivered.notifications?.filter((n) => n.id === WORK_TRACKING_NOTIFICATION_ID) ?? []

    if (ourDelivered.length > 0) {
      await LocalNotifications.removeDeliveredNotifications({ notifications: ourDelivered })
    }

    // Stop tracking the location
    await stopLocationTracking()
  }
}

export { startTracking, stopTracking }
